"use client";

import { ApolloProvider } from "@apollo/client";
import { GoogleTagManager } from "@next/third-parties/google";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Datadog } from "@yardzen/components/datadog";
import { YzThemeProvider } from "@yardzen/components/theme-provider";
import {
  getSplitUserKey,
  getYzsaId,
  GQLClient,
  initializeSplitForGTM,
} from "@yardzen/next-client-util";
import { SessionProvider } from "next-auth/react";
import { ReactNode, useEffect } from "react";
import { PageViewTracker } from "../../components/PageViewTracker";
import { YzsaSync } from "../../components/YzsaSync";
import { ClientAuthChecker } from "./ClientAuthChecker";
import MainContent from "./MainContent";

const client = GQLClient();
const googleTagManagerId = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID;

export default function ClientsideLayout(props: {
  mustAcceptTerms: boolean;
  userId: string | undefined;
  children: ReactNode;
  treatments?: Record<string, string>;
}) {
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

  // Initialize Split client for GTM (Harness FME approach)
  useEffect(() => {
    const splitClientKey = process.env.NEXT_PUBLIC_SPLIT_CLIENT_KEY;

    if (!splitClientKey) {
      console.warn("[Split GTM] NEXT_PUBLIC_SPLIT_CLIENT_KEY not configured");
      return;
    }

    const anonymousId = getYzsaId();
    const userKey = getSplitUserKey(props.userId, anonymousId);

    // Initialize Split client and push to GTM dataLayer
    initializeSplitForGTM(splitClientKey, userKey).catch((error) => {
      console.error("[Split GTM] Failed to initialize:", error);
    });
  }, [props.userId]);

  return (
    <>
      {googleTagManagerId && (
        <GoogleTagManager
          gtmId={googleTagManagerId}
          gtmScriptUrl="https://yardwork.yardzen.com/gtm.js"
        />
      )}

      {siteKey && (
        <script
          src={`https://www.google.com/recaptcha/api.js?render=${siteKey}&badge=inline`}
          async
          defer
        />
      )}

      <APIProvider
        apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!}
        onLoad={() => console.log("Google Maps API has loaded.")}
      >
        <YzsaSync />
        <SessionProvider refetchInterval={60} refetchOnWindowFocus>
          <Datadog service="build-marketplace" treatments={props.treatments} />
          <ClientAuthChecker />
          <PageViewTracker />
          <ApolloProvider client={client}>
            <YzThemeProvider>
              <MainContent
                userId={props.userId}
                mustAcceptTerms={props.mustAcceptTerms}
              >
                {props.children}
              </MainContent>
            </YzThemeProvider>
          </ApolloProvider>
        </SessionProvider>
      </APIProvider>
    </>
  );
}
